<script setup lang="ts">
import { computed, onBeforeUnmount, ref, useSlots } from 'vue';
import { NxTooltip } from '../Tooltip';
import { bandClipPath } from './calendarBands';
import { buildMonthCells, DAYS_PER_WEEK, isoWeekNumber } from './calendarGrid';
import { dayKeyAction } from './calendarKeys';
import type { MonthCell } from './calendarGrid';
import type { MonthDensity, NxCalendarDay, NxCalendarMarker } from './types';

const props = defineProps<{
  year: number;
  /** 1-12. */
  month: number;
  dayMap: Map<string, NxCalendarDay>;
  firstDayOfWeek: 1 | 7;
  locale: string;
  showWeekNumbers: boolean;
  /** Everything the view decides about rendering a month; see MonthDensity. */
  density: MonthDensity;
  /** Heading of the week column, localised by the parent. '' leaves it blank. */
  weekPrefix: string;
  /** Accessible name of this month's grid, e.g. "Januar 2026". */
  monthLabel: string;
  /** Renders every cell as a button and turns the key handling on. */
  selectable: boolean;
  /** The one cell that holds `tabindex="0"`; the parent owns this state. */
  activeDate: string | null;
  /** Today as ISO `YYYY-MM-DD`. The parent owns the clock — see NxCalendar. */
  today: string;
  /** Opt-in: the day drawn as selected. */
  selectedDate?: string | null;
  /** Opt-in: ISO range, both ends inclusive, drawn as a tint. */
  highlightRange?: { start: string; end: string } | null;
}>();

const emit = defineEmits<{ dayClick: [date: string]; focusDate: [date: string] }>();

const slots = useSlots();

type RenderCell = MonthCell & {
  /** Named `decoration`, not `day`: `MonthCell.day` is already the day NUMBER. */
  decoration: NxCalendarDay | undefined;
  isToday: boolean;
  /**
   * Drawn as a weekend column: Saturday or Sunday (a property of the calendar),
   * or a weekday the consumer flagged `off` (a property of its domain).
   */
  isWeekend: boolean;
  /** The bands actually drawn, in order. */
  bands: NxCalendarMarker[];
  /** The day's state dot, if it carries one — at most one, by the type. */
  dot: NxCalendarMarker | undefined;
  /**
   * An adjacent-month cell the default rendering leaves empty. Whether it is
   * really inert also depends on the `day` slot, which only the render knows —
   * see `isInert`.
   */
  blank: boolean;
  /** Date, then the day's own label, then every marker label — each text once. */
  name: string;
};

/**
 * One formatter for the whole grid. Built per cell it would be constructed
 * hundreds of times per render in the year view — `Intl.DateTimeFormat` is the
 * expensive part of naming a day, not the formatting.
 */
const dateFormatter = computed(() => new Intl.DateTimeFormat(props.locale, { dateStyle: 'long' }));

const cells = computed<RenderCell[]>(() => {
  const today = props.today;
  const { maxMarkers, showAdjacentDays } = props.density;
  const formatter = dateFormatter.value;
  return buildMonthCells(props.year, props.month, props.firstDayOfWeek).map((cell, index) => {
    const decoration = props.dayMap.get(cell.iso);
    const markers = decoration?.markers ?? [];
    // The raster runs week by week, so the column IS the weekday: Saturday and
    // Sunday are the last two when the week starts on Monday, the first and the
    // last when it starts on Sunday. No Date needed to tell a weekend.
    const column = index % DAYS_PER_WEEK;
    // A weekday the consumer flagged `off` joins the weekend through the SAME
    // flag rather than a class of its own: `.is-weekend` then stays the one
    // selector every weekend rule hangs on (the grid tint here, the mini
    // month's reset further down), and no rule can be added for the weekend
    // that a free Friday silently misses.
    const isWeekend =
      (props.firstDayOfWeek === 1 ? column >= 5 : column === 0 || column === DAYS_PER_WEEK - 1) ||
      decoration?.off === true;
    // Hard budget: what does not fit is not drawn. It is still named in the
    // tooltip and the accessible name below, so nothing about the day is lost —
    // only the paint stops at three, because a fourth band in a 30px cell is a
    // stripe nobody can read.
    const bands = markers.slice(0, maxMarkers);
    // One statement, however often it was entered: the same text on the day and
    // on a marker is a repeat, not a second thing the day is. It matters here and
    // not only in the tooltip, because this string is the cell's accessible name —
    // a screen reader says it once per copy. First occurrence wins, so the day's
    // own label keeps its place ahead of the marker labels.
    // The dot's label comes LAST: the bands name what the day is, the dot only
    // adds a state on top of that, and reading the state before the thing it
    // qualifies gets the sentence the wrong way round.
    const labels = [
      ...new Set(
        [
          decoration?.label,
          ...markers.map((marker) => marker.label),
          decoration?.dot?.label,
        ].filter(Boolean)
      ),
    ];
    return {
      ...cell,
      decoration,
      isToday: cell.iso === today,
      isWeekend,
      bands,
      dot: decoration?.dot,
      blank: !cell.inMonth && !showAdjacentDays,
      name: [formatter.format(new Date(cell.year, cell.month - 1, cell.day)), ...labels].join(', '),
    };
  });
});

/**
 * The raster in rows, because a row is what BOTH the layout and ARIA need: a
 * `role="grid"` without `role="row"` between grid and gridcell is not a grid,
 * and the week number is that row's header rather than a floating cell.
 */
const weeks = computed(() => {
  // The number belongs to the row's MONDAY, not to its first cell: an ISO week
  // always runs Monday to Sunday, so a Sunday-first row opens in the week BEFORE
  // the one it mostly shows — and would print that lower number for all seven
  // days. The raster is weekday-aligned, so the Monday is column 0 when the week
  // starts on Monday and column 1 when it starts on Sunday.
  const monday = props.firstDayOfWeek === 1 ? 0 : 1;
  const rows: Array<{ number: number; cells: RenderCell[] }> = [];
  for (let start = 0; start < cells.value.length; start += DAYS_PER_WEEK) {
    const rowCells = cells.value.slice(start, start + DAYS_PER_WEEK);
    const anchor = rowCells[monday];
    rows.push({ number: isoWeekNumber(anchor.year, anchor.month, anchor.day), cells: rowCells });
  }
  return rows;
});

const weekdayLabels = computed(() => {
  const formatter = new Intl.DateTimeFormat(props.locale, { weekday: props.density.weekdayFormat });
  // 5 January 2026 is a Monday; offset by the configured week start.
  return Array.from({ length: DAYS_PER_WEEK }, (_, index) => {
    const shift = props.firstDayOfWeek === 1 ? index : index - 1;
    return formatter.format(new Date(2026, 0, 5 + shift));
  });
});

function cellClasses(cell: RenderCell): string[] {
  // An inert cell wears no decoration and no today ring: the date it stands for
  // has its own cell in its own month block, and painting it here would show the
  // same holiday — or today — twice in one year view.
  const inert = isInert(cell);
  const classes: string[] = [];
  if (!inert && cell.bands.length) classes.push('has-bands');
  // The day number's ink follows what lies under it: over a solid band it is the
  // on-colour, over nothing but pale hatching it would vanish. One solid band is
  // enough, because that band is what the number sits on — hence "some", and
  // hence the drawn bands rather than every marker the day was handed.
  if (!inert && cell.bands.some((band) => band.pattern !== 'hatched')) {
    classes.push('has-solid-band');
  }
  if (cell.isWeekend) classes.push('is-weekend');
  if (!cell.inMonth) classes.push('is-outside');
  if (cell.isToday && !inert) classes.push('is-today');
  if (!inert && props.selectedDate && cell.iso === props.selectedDate) classes.push('is-selected');
  const range = props.highlightRange;
  // ISO dates compare as strings, so the range needs no Date at all.
  if (!inert && range && cell.iso >= range.start && cell.iso <= range.end) {
    classes.push('is-in-range');
    // The mini month draws the range as one band with rounded ends.
    if (cell.iso === range.start) classes.push('is-range-start');
    if (cell.iso === range.end) classes.push('is-range-end');
  }
  if (props.density.compact) classes.push('is-compact');
  return classes;
}

/**
 * A cell that shows nothing does nothing. In the year view the adjacent-month
 * fillers carry no day number, so they must not be buttons, must not answer a
 * click or a key, and must not be announced — they exist to hold the raster's
 * shape. Only the default rendering can be judged this way: a `day` slot may
 * well put something in an outside cell, and then it stays live. That slot is
 * why this is not precomputed with the cell: slot presence is answered by the
 * render, and a computed would not see it change.
 */
function isInert(cell: RenderCell): boolean {
  return cell.blank && !slots.day;
}

/**
 * The tone class stays on a hatched band too: it sets the fallback colour for a
 * marker that brings no `color` of its own. Forced-colours mode reads no class
 * here — the bands keep their authored colours there through
 * `forced-color-adjust: none` (see the `forced-colors` block below). `is-hatched`
 * only swaps the fill, so the pattern and the colour stay two independent
 * statements.
 */
function bandClasses(marker: NxCalendarMarker): string[] {
  const classes = ['nx-calendar-band', `tone-${marker.tone}`];
  if (marker.pattern === 'hatched') classes.push('is-hatched');
  return classes;
}

/**
 * A band's own colour, when the consumer brought one. The tone class stays on
 * the element either way — it is what the fallback and forced-colours mode
 * read; `--band` merely outranks the token the class sets, because the kit has
 * no vocabulary for what a day IS and cannot name `company_closure` blue. Only
 * set when there is a colour, so a plain marker keeps the tone token untouched.
 */
function bandStyle(marker: NxCalendarMarker, index: number, total: number): Record<string, string> {
  const style: Record<string, string> = { clipPath: bandClipPath(index, total) };
  if (marker.color) style['--band'] = marker.color;
  return style;
}

/**
 * The dot's classes and colour work exactly like a band's — same tone class,
 * same `--dot` seam — so a consumer palette reaches both shapes the same way and
 * forced-colours mode still has a tone to fall back on.
 */
function dotClasses(marker: NxCalendarMarker): string[] {
  return ['nx-calendar-dot', `tone-${marker.tone}`];
}

function dotStyle(marker: NxCalendarMarker): Record<string, string> {
  return marker.color ? { '--dot': marker.color } : {};
}

/**
 * The day tooltip is the kit's, not the browser's: `title` renders in the OS
 * style, after its own delay, and it cannot be told to match anything. One
 * NxTooltip serves the whole month and is moved from cell to cell, because a
 * wrapper per day would cost forty-two nodes and push the gridcells out of
 * their rows. Delegation for the same reason — one listener pair per grid.
 */
const hovered = ref<HTMLElement | null>(null);
const hoveredText = ref('');
let hoverTimer: ReturnType<typeof setTimeout> | undefined;

/** Long enough that sweeping the raster does not strobe, short enough to feel free. */
const HOVER_DELAY_MS = 300;

function onPointerOver(event: MouseEvent | FocusEvent): void {
  const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[role="gridcell"]');
  if (!target || target === hovered.value) return;
  const cell = cells.value.find((candidate) => candidate.iso === target.dataset.date);
  if (!cell || isInert(cell)) return;
  clearTimeout(hoverTimer);
  // A bubble already up follows immediately; a fresh one waits out the delay, so
  // crossing the grid does not leave a trail of tooltips behind.
  const move = () => {
    hovered.value = target;
    hoveredText.value = cell.name;
  };
  if (hovered.value) move();
  else hoverTimer = setTimeout(move, HOVER_DELAY_MS);
}

function onPointerOut(event: MouseEvent | FocusEvent): void {
  const next = (event as MouseEvent).relatedTarget as HTMLElement | null;
  if (next?.closest('[role="gridcell"]')) return;
  clearTimeout(hoverTimer);
  hovered.value = null;
}

onBeforeUnmount(() => clearTimeout(hoverTimer));

/** Computes the next date only; the parent owns where the focus actually goes. */
function onKeydown(event: KeyboardEvent, cell: RenderCell): void {
  if (!props.selectable || isInert(cell)) return;
  // A modified key belongs to the browser or the host app — Alt+Left is Back,
  // Cmd+Left is on macOS — and the ARIA grid pattern leaves every combination it
  // has not claimed alone. Unguarded, the grid moved its cursor and swallowed
  // the navigation with preventDefault.
  if (event.ctrlKey || event.altKey || event.metaKey) return;
  const action = dayKeyAction(event.key, cell.iso, props.firstDayOfWeek);
  if (!action) return;
  event.preventDefault();
  if (action.kind === 'select') emit('dayClick', cell.iso);
  else emit('focusDate', action.date);
}
</script>

<template>
  <div
    class="nx-calendar-month"
    :class="{
      'has-weeks': showWeekNumbers,
      'is-stretched': density.stretch,
      'is-mini': density.mini,
    }"
    role="grid"
    :aria-label="monthLabel"
    @focusin="onPointerOver"
    @focusout="onPointerOut"
    @mouseout="onPointerOut"
    @mouseover="onPointerOver"
  >
    <div class="nx-calendar-row" role="row">
      <div v-if="showWeekNumbers" class="nx-calendar-week-head" role="columnheader">
        {{ weekPrefix }}
      </div>
      <div
        v-for="(weekday, index) in weekdayLabels"
        :key="`wd-${index}`"
        class="nx-calendar-weekday"
        role="columnheader"
      >
        {{ weekday }}
      </div>
    </div>

    <div v-for="week in weeks" :key="week.cells[0].iso" class="nx-calendar-row" role="row">
      <div v-if="showWeekNumbers" class="nx-calendar-week" role="rowheader">
        {{ week.number }}
      </div>
      <component
        :is="selectable && !isInert(cell) ? 'button' : 'div'"
        v-for="cell in week.cells"
        :key="cell.iso"
        :aria-current="cell.isToday && !isInert(cell) ? 'date' : undefined"
        :aria-hidden="isInert(cell) ? 'true' : undefined"
        :aria-label="isInert(cell) ? undefined : cell.name"
        :class="['nx-calendar-cell', ...cellClasses(cell)]"
        :data-date="isInert(cell) ? undefined : cell.iso"
        role="gridcell"
        :tabindex="
          selectable && !isInert(cell)
            ? cell.inMonth && cell.iso === activeDate
              ? 0
              : -1
            : undefined
        "
        :type="selectable && !isInert(cell) ? 'button' : undefined"
        @click="selectable && !isInert(cell) && emit('dayClick', cell.iso)"
        @keydown="onKeydown($event, cell)"
      >
        <slot
          :date="cell.iso"
          :day="cell.decoration"
          :in-month="cell.inMonth"
          :is-today="cell.isToday"
          name="day"
        >
          <span
            v-if="!isInert(cell) && cell.bands.length"
            class="nx-calendar-bands"
            aria-hidden="true"
          >
            <span
              v-for="(marker, mi) in cell.bands"
              :key="`b-${mi}`"
              :class="bandClasses(marker)"
              :style="bandStyle(marker, mi, cell.bands.length)"
            ></span>
          </span>
          <!-- After the bands and before the number: both are positioned, so DOM
               order decides the stacking — the dot has to sit over the paint it
               qualifies, and under nothing. `aria-hidden` because its label is
               already in the cell's own name, like a band's. -->
          <span
            v-if="!isInert(cell) && cell.dot"
            :class="dotClasses(cell.dot)"
            :style="dotStyle(cell.dot)"
            aria-hidden="true"
          ></span>
          <span v-if="cell.inMonth || density.showAdjacentDays" class="nx-calendar-daynum">
            {{ cell.day }}
          </span>
        </slot>
      </component>
    </div>

    <!-- Only while a day is actually hovered: mounted permanently, each of the
         twelve grids in the year view would keep a bubble and its position
         watcher alive for nothing. -->
    <NxTooltip v-if="hovered" :anchor="hovered" :open="true" side="top" :text="hoveredText" />
  </div>
</template>

<style scoped>
/* A month is one framed table, NX corners included. The inner rules are the
   1px gaps letting the frame colour through, not borders on the cells: a cell
   border would double against the frame, and a radius cannot clip a line that
   the cell itself draws. Every child therefore needs an opaque background. */
.nx-calendar-month {
  /* Every row repeats the same track list, so one custom property holds it: the
     header row and the day rows have to agree on the columns or the week column
     would be a different width in each of them. */
  --month-columns: repeat(7, minmax(0, 1fr));
  display: grid;
  gap: var(--nxt-hairline);
  background: var(--nxt-table-border);
  /* Two hairlines, so the block reads as a framed table instead of dissolving
     into its own inner rules — those are one hairline wide and the same colour.
     The colour stays the weekday band's: --nxt-table-border IS --nxt-table-
     header-bg in the light theme, and unlike it stays visible in the dark one. */
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-table-border);
  border-radius: var(--nxt-radius-md);
  overflow: hidden;
}

/* The week column had the width of its content (~38px with the prefix in the
   cells); twice that, as a definite track, so the numbers have room to sit
   centred and the day columns share what is left. */
.nx-calendar-month.has-weeks {
  --month-columns: calc(var(--nxt-size-unit) * 4.5) repeat(7, minmax(0, 1fr));
}

/* A row is a grid of its own rather than `display: contents`: the cells stay
   aligned across rows because every row uses the same tracks, and the role
   survives in every a11y tree — `display: contents` has been dropped from some
   of them. The container's own gap draws the rules BETWEEN rows, this one draws
   them between cells. */
.nx-calendar-row {
  display: grid;
  grid-template-columns: var(--month-columns);
  gap: var(--nxt-hairline);
}

/* Stretched, the month takes its size from the box around it: the header row
   stays content-sized and every day row shares what is left, exactly as the
   Calendar module's grid does. The `1fr` keeps that — a box TALLER than the
   floor below is filled, never scrolled.

   The floor is what stops the fall the other way: 100% of an auto height is
   auto, so without one the rows collapsed to 28px whenever no consumer gave a
   height (measured in I-1200, fixed in I-1515). It sits here, on the grid,
   because this is the content of the scroller in `NxCalendar.vue`
   (`.nx-calendar.view-month`): only a floor INSIDE a scroller can be scrolled
   to: on the scroller or on the root it is the scroller that cannot shrink, and
   a box smaller than the floor gets overflow instead of a scrollbar.

   32 units, `border-box` so the two hairlines count. The old hand-drawn frames
   gave 36 units to the WHOLE calendar; the grid's share of that is 36 units less
   the header and its `space-sm` — 527px in Chromium at scale 1, i.e. a hair
   under 33 units. It is ROUNDED DOWN to 32, deliberately: the header's height
   cannot be computed here (a button is `calc(1lh + 2 * space-xs)`, so font
   metrics decide it), and a floor rounded up would put a scrollbar into a box
   that is exactly frame-sized. The price is that a consumer without a frame gets
   561px where the frames gave 576px — one size unit shorter, and the same for
   a five-row and a six-row month. */
.nx-calendar-month.is-stretched {
  box-sizing: border-box;
  height: 100%;
  min-height: calc(var(--nxt-size-unit) * 32);
  grid-template-rows: auto;
  grid-auto-rows: minmax(0, 1fr);
}

/* Tall cells put the day at the top and leave the rest to what the day carries;
   centring a number in a 90px box reads as a typo. */
.is-stretched .nx-calendar-cell {
  align-items: flex-start;
  justify-content: flex-start;
  min-height: 0;
  padding: var(--nxt-space-2xs);
}

/* The day number becomes a badge, so today can be a filled circle instead of a
   ring around the whole cell — the Calendar module's most recognisable cue. */
.is-stretched .nx-calendar-daynum {
  display: inline-grid;
  place-items: center;
  width: var(--nxt-icon-size-lg);
  height: var(--nxt-icon-size-lg);
  border-radius: 50%;
  font-weight: var(--nxt-font-weight-semibold);
}

/* The badge replaces the ring — except while the cell has focus. Three classes
   outrank `button…:focus-visible`, so without the `:not()` this rule swallowed
   the focus ring on exactly one cell of the month: today. */
.is-stretched .nx-calendar-cell.is-today:not(:focus-visible) {
  outline: none;
}

/* Over hatched bands ONLY the pale tint is under the number most of the time,
   and the banded day-number ink (`--nxt-neutral-fg`, white in light, set further
   down) would vanish on it. A cell whose every band is hatched therefore keeps
   the body ink; one solid band is enough to switch back, because that band is
   what the number sits on.

   ⚠️ Placement and selector are deliberate. The rule outranks the banded ink
   (0,4,0 against 0,3,0) wherever it stands, but it must NOT outrank the two
   badges that fill the number with brand colour: the selected day is excluded
   by name, and today's badge — same specificity — is the very next rule, so it
   wins by order in the stretched month, and so do the forced-colours and mini
   today rules further down. Moved below any of them, a hatched today would get
   dark ink on the brand fill. The `:not()` list counts as one class. */
.nx-calendar-cell.has-bands:not(.has-solid-band, .is-selected) .nx-calendar-daynum {
  color: var(--nxt-fg);
}

.is-stretched .nx-calendar-cell.is-today .nx-calendar-daynum {
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}

/* Adjacent-month days recede by surface, not just by text colour — at this size
   a grey number alone does not carry. */
.is-stretched .nx-calendar-cell.is-outside {
  background: var(--nxt-surface-sunken-bg);
}

/* Fade the PAINT, never the text: the surface still has to carry the grid's
   rules and the weekend rhythm, while a marker on an adjacent-month day reads
   visibly weaker than the same marker inside the month. That half-strength band
   is the recession — the eye should read the selected month as one block and
   everything around it as the edge it bleeds into.

   Half opacity used to sit on everything the cell shows (`> *`), which put a
   plain day number at 2.13:1 against the sunken surface and a banded one at
   1.13:1 — far under the 4.5:1 the guidelines ask of body text, and in this view
   the cells are live buttons, not decoration. Fading only the bands and leaving
   the paired on-colour on top was no better at 2.06-2.41: a half-transparent
   band is a PALE surface and wants dark ink, not light. Hence the rule below. */
.is-stretched .nx-calendar-cell.is-outside .nx-calendar-bands {
  opacity: 0.5;
}

/* And no badge geometry on a day that is not this month's — a second circle
   would compete with today's. */
.is-stretched .nx-calendar-cell.is-outside .nx-calendar-daynum {
  width: auto;
  height: auto;
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-regular);
}

/* Over a HALF-STRENGTH band the number takes the body colour, not the tone's
   paired on-colour: the faded band is a light tint in either theme, so the
   readable pairing is the ordinary ink. --nxt-fg-subtle from the rule above is
   not enough over a tint (2.53-2.96), the on-colour is the wrong direction
   entirely (2.06-2.41), and this lands at 5.70 or better in every tone. The
   badge geometry stays off. */
.is-stretched .nx-calendar-cell.is-outside.has-bands .nx-calendar-daynum {
  color: var(--nxt-fg);
}

/* Weekday band and week column ARE the table header: same tokens as NxTable's
   `th`, so retheming the table carries the calendar with it. */
.nx-calendar-weekday,
.nx-calendar-week,
.nx-calendar-week-head {
  background: var(--nxt-table-header-bg);
  color: var(--nxt-table-header-fg);
  font-size: var(--nxt-table-size);
  font-weight: var(--nxt-font-weight-semibold);
  text-align: center;
  padding: var(--nxt-space-2xs) 0;
}

/* Centred on both axes, not just horizontally: stretched rows are tall, and a
   text-align alone would leave the number stuck to the top edge. */
.nx-calendar-week,
.nx-calendar-week-head {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Strong rule under the header, the way the table separates head from body. */
.nx-calendar-weekday,
.nx-calendar-week-head {
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-strong);
}

.nx-calendar-cell {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--nxt-space-2xs);
  min-height: var(--nxt-control-size-md);
  padding: var(--nxt-space-2xs) 0;
  background: var(--nxt-table-row-bg);
  color: var(--nxt-table-cell-fg);
  font-size: var(--nxt-table-size);
}

/* Saturday and Sunday recede as whole columns — the strongest single cue the
   legacy calendar used to make a month readable at a glance. The table's zebra
   tint carries it: --nxt-neutral-subtle-bg is one step off white and the columns
   all but vanished. A day's bands cover it, because the weekend is the weakest
   layer of the three. */
.nx-calendar-cell.is-weekend {
  background: var(--nxt-table-zebra-bg);
}

/* Selectable cells are real buttons; strip the UA chrome the grid must not show. */
/* Selectable cells are real buttons; strip the UA chrome the grid must not show.
   The border goes entirely — the rules are the grid's gaps, not the cell's. */
button.nx-calendar-cell {
  appearance: none;
  border: 0;
  font: inherit;
  font-size: var(--nxt-table-size);
  cursor: pointer;
}

/* Inset ring: the block clips its overflow for the rounded frame, so a ring
   drawn outside the cell would be cut off on the outer rows and columns. */
button.nx-calendar-cell:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-size-unit) * -0.125);
}

button.nx-calendar-cell:hover {
  background: var(--nxt-table-row-hover-bg);
}

/* The year cell stays a rectangle rather than a square: the legacy calendar's
   rows are wider than tall, and the block's own max-width keeps the ratio in
   range instead of letting the cell grow with the container. border-box so the
   row IS this height — with content-box the padding would add on top. */
.nx-calendar-cell.is-compact {
  box-sizing: border-box;
  min-height: calc(var(--nxt-size-unit) * 2);
}

/* Outside days stay readable but recede; the year view hides their number. */
.nx-calendar-cell.is-outside {
  color: var(--nxt-fg-subtle);
}

/* Today is a ring, never a fill — a fill would compete with the day's meaning. */
.nx-calendar-cell.is-today {
  outline: var(--nxt-hairline) solid var(--nxt-brand-bg);
  outline-offset: calc(var(--nxt-size-unit) * -0.0625);
}

/* The opt-in range tint and selection (the sidebar month): the range is a
   quiet brand wash under the day, the selected day a filled number on top of
   it — the same badge the stretched month uses for today, so a compact month
   needs no second vocabulary. Today keeps its ring. */
.nx-calendar-cell.is-in-range:not(.has-bands) {
  background: var(--nxt-brand-subtle-bg);
}

.nx-calendar-cell.is-selected .nx-calendar-daynum {
  display: inline-grid;
  place-items: center;
  min-width: var(--nxt-icon-size-md);
  height: var(--nxt-icon-size-md);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  font-weight: var(--nxt-font-weight-semibold);
}

/* Markers are diagonal bands across the whole cell — one band per statement, so
   a day that is three things shows three of them. Drawn as elements rather than
   one gradient: the count is what carries the meaning, and an element can be
   counted, titled and faded. Each band covers the cell and is clipped to its own
   slice, so every share is measured against the cell itself. */
.nx-calendar-bands {
  position: absolute;
  inset: 0;
}

/* `--band` is the seam a marker's own `color` writes into: an inline custom
   property outranks every rule here, so a consumer palette needs no class of
   its own and the tone token stays the fallback. */
.nx-calendar-band {
  --band: var(--nxt-neutral-bg);
  position: absolute;
  inset: 0;
  background: var(--band);
}

.nx-calendar-band.tone-info {
  --band: var(--nxt-info-bg);
}

.nx-calendar-band.tone-success {
  --band: var(--nxt-success-bg);
}

.nx-calendar-band.tone-warning {
  --band: var(--nxt-warning-bg);
}

.nx-calendar-band.tone-danger {
  --band: var(--nxt-danger-bg);
}

/* The third marker SHAPE: a hatched band. Thin stripes of the band colour over
   a pale tint of the same colour, so the fill still reads as "this colour" at a
   glance while the stripes say "not settled yet" — a filed leave request next to
   an approved one in the same hue. The tint mixes into the cell's own background
   rather than a fixed white, so it stays pale in both themes. Stripe geometry in
   size units, like the dot, so it follows the scope scale. */
.nx-calendar-band.is-hatched {
  background:
    repeating-linear-gradient(
      135deg,
      var(--band) 0,
      var(--band) calc(var(--nxt-size-unit) * 0.125),
      transparent calc(var(--nxt-size-unit) * 0.125),
      transparent calc(var(--nxt-size-unit) * 0.375)
    ),
    color-mix(in srgb, var(--band) 28%, var(--nxt-table-row-bg));
}

/* The second marker SHAPE: one dot in the top-right corner, over the bands. A
   band says what a day IS and can be one of several; a dot says one thing about
   the day's STATE and is there or it is not — two different questions, so two
   different shapes rather than a fifth band nobody could tell from the four
   below it.

   `--dot` is the same seam as the band's `--band`: an inline custom property
   from the consumer's palette outranks every rule here, and the tone class stays
   as the fallback.

   The ring is what keeps the shape readable, and it is the CELL's background,
   not a border colour: the dot sits on top of a band whose colour the kit does
   not know, and a dot in the same hue as the band underneath would otherwise
   dissolve into it. Drawn as a box-shadow so it costs no layout and no second
   element.

   Sized in `--nxt-size-unit` like the rest of the component (7px at scale 1), so
   it follows the scope scale instead of staying 7px in a scaled-up screen. */
.nx-calendar-dot {
  --dot: var(--nxt-neutral-bg);
  position: absolute;
  /* `xs`, not `2xs`: with the ring the dot sat pressed into the corner (André,
     23.09.2026) — it wants the same breathing room the day number has. */
  top: var(--nxt-space-xs);
  right: var(--nxt-space-xs);
  /* 10px at scale 1 — André's call on 23.09.2026 after seeing 7px on the grid:
     the mockup's dot reads at a glance, a 7px one had to be looked for. The
     ring is two hairlines for the same reason. */
  width: calc(var(--nxt-size-unit) * 0.625);
  height: calc(var(--nxt-size-unit) * 0.625);
  border-radius: 50%;
  background: var(--dot);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-table-row-bg);
}

.nx-calendar-dot.tone-info {
  --dot: var(--nxt-info-bg);
}

.nx-calendar-dot.tone-success {
  --dot: var(--nxt-success-bg);
}

.nx-calendar-dot.tone-warning {
  --dot: var(--nxt-warning-bg);
}

.nx-calendar-dot.tone-danger {
  --dot: var(--nxt-danger-bg);
}

/* The year cell is a third of the height and carries no padding on its sides, so
   the month view's dot would eat the day number. 6px at scale 1, tucked into the
   corner by two hairlines — still a dot, no longer a blot. */
.is-compact .nx-calendar-dot {
  /* `2xs` rather than a hairline multiple: the same step down from the month
     view's `xs` that the dot's size takes, so it keeps its room in the corner
     instead of touching the cell edge (André, 23.09.2026). */
  top: var(--nxt-space-2xs);
  right: var(--nxt-space-2xs);
  width: calc(var(--nxt-size-unit) * 0.375);
  height: calc(var(--nxt-size-unit) * 0.375);
}

/* Over a band the number needs the paired on-colour. Every tone's `-fg` is the
   same within a theme — white in light, near-black in dark — so one value serves
   whatever mix of bands a day happens to carry. `position` lifts it above the
   absolutely positioned wrapper. */
.nx-calendar-cell.has-bands .nx-calendar-daynum {
  position: relative;
  color: var(--nxt-neutral-fg);
  font-weight: var(--nxt-font-weight-semibold);
}

/* Forced colours ignore every CSS background, and this component says two things
   with one: the hairline gaps that draw the table's rules, and a band's tone —
   the whole visible statement a decorated day makes. Both would disappear
   without a trace. The rules come back as an inset system-coloured line, an
   outline so it costs no layout; the bands and the number that has to stay
   readable on top of them keep their own colours, which is the case
   forced-color-adjust exists for. Today and focus outrank this on specificity
   and keep their own ring. */
/* ── The sidebar month (`density.mini`) ─────────────────────────────────────
   A small month next to a navigation, not a table: no frame, no rules, no
   weekend columns, no header band. The days are round buttons, the selected day
   a filled circle, today a ring, the range the main view shows one quiet band
   with rounded ends, and the dot sits under the number instead of in a corner
   it would share with the digits. Sized for a 272px sidebar: seven ~34px
   columns, 28px circles. */
.nx-calendar-month.is-mini {
  --mini-day: calc(var(--nxt-size-unit) * 1.75);
  /* Mixed from the brand rather than --nxt-brand-subtle-bg, which is all but
     black on the dark surface. */
  --mini-range-bg: color-mix(in srgb, var(--nxt-brand-bg) 12%, transparent);
  gap: var(--nxt-hairline);
  background: transparent;
  border: 0;
  border-radius: 0;
  overflow: visible;
}

.is-mini .nx-calendar-row {
  gap: 0;
}

.is-mini .nx-calendar-weekday {
  padding: var(--nxt-space-2xs) 0;
  background: transparent;
  border: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.is-mini .nx-calendar-cell,
.is-mini .nx-calendar-cell.is-weekend {
  overflow: visible;
  min-height: calc(var(--mini-day) + var(--nxt-space-2xs));
  padding: 0;
  background: transparent;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-xs);
}

.is-mini .nx-calendar-daynum {
  display: inline-grid;
  place-items: center;
  width: var(--mini-day);
  height: var(--mini-day);
  border-radius: var(--nxt-radius-full);
  font-variant-numeric: tabular-nums;
  transition: background-color 0.12s;
}

.is-mini .nx-calendar-cell.is-outside {
  color: var(--nxt-fg-muted);
}

/* The range: one band across its days, rounded where it starts and ends and
   at the week's edges. Behind the circles, so a selected day sits on it. */
.is-mini .nx-calendar-cell.is-in-range:not(.has-bands) {
  background: var(--mini-range-bg);
}
.is-mini .nx-calendar-cell.is-in-range.is-range-start,
.is-mini .nx-calendar-cell.is-in-range:first-child {
  border-start-start-radius: var(--nxt-radius-full);
  border-end-start-radius: var(--nxt-radius-full);
}
.is-mini .nx-calendar-cell.is-in-range.is-range-end,
.is-mini .nx-calendar-cell.is-in-range:last-child {
  border-start-end-radius: var(--nxt-radius-full);
  border-end-end-radius: var(--nxt-radius-full);
}

/* Hover and focus land on the circle, not on a square cell. */
.is-mini button.nx-calendar-cell:hover {
  background: transparent;
}
.is-mini button.nx-calendar-cell.is-in-range:hover {
  background: var(--mini-range-bg);
}
.is-mini button.nx-calendar-cell:hover .nx-calendar-daynum {
  background: var(--nxt-input-bg-hover);
}
.is-mini button.nx-calendar-cell:focus-visible {
  outline: none;
}
.is-mini button.nx-calendar-cell:focus-visible .nx-calendar-daynum {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * -1);
}

/* Today: a ring round the number, in brand ink. */
.is-mini .nx-calendar-cell.is-today {
  outline: none;
}
.is-mini .nx-calendar-cell.is-today .nx-calendar-daynum {
  color: var(--nxt-brand-bg);
  font-weight: var(--nxt-font-weight-semibold);
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 1.5) var(--nxt-brand-bg);
}

/* Selected: the filled circle (after today, so a selected today is filled). */
.is-mini .nx-calendar-cell.is-selected .nx-calendar-daynum,
.is-mini button.nx-calendar-cell.is-selected:hover .nx-calendar-daynum {
  min-width: 0;
  height: var(--mini-day);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  font-weight: var(--nxt-font-weight-semibold);
  box-shadow: none;
}

/* The dot: small, centred under the number, inside the circle's lower edge. */
.is-mini .nx-calendar-dot {
  top: auto;
  right: auto;
  bottom: calc(50% - var(--mini-day) / 2 + var(--nxt-hairline) * 3);
  left: 50%;
  z-index: 1;
  width: calc(var(--nxt-size-unit) * 0.25);
  height: calc(var(--nxt-size-unit) * 0.25);
  box-shadow: none;
  transform: translateX(-50%);
}
.is-mini .nx-calendar-cell.is-selected .nx-calendar-dot {
  background: var(--nxt-brand-fg);
}
.is-mini .nx-calendar-cell.is-outside .nx-calendar-dot {
  opacity: 0.5;
}

@media (forced-colors: active) {
  .nx-calendar-cell {
    outline: var(--nxt-hairline) solid canvastext;
    outline-offset: calc(var(--nxt-hairline) * -1);
  }

  /* The property inherits, so the wrapper covers every band inside it. */
  .nx-calendar-bands,
  .nx-calendar-cell.has-bands .nx-calendar-daynum {
    forced-color-adjust: none;
  }

  /* The dot is a statement of its own and would vanish with its background like
     the bands do. It keeps the shape rather than a colour: the forced palette
     has no room for five tones, so every dot is simply ink on the canvas, and
     the ring in `canvas` is what still separates it from the band beneath. */
  .nx-calendar-dot {
    forced-color-adjust: none;
    background: canvastext;
    box-shadow: 0 0 0 var(--nxt-hairline) canvas;
  }

  /* Today loses both of its cues here, one per view: the month view drops the
     ring on purpose and marks the day with a filled badge, whose background the
     forced palette discards, and the year view's single-hairline ring is now
     what EVERY cell wears because of the rule above. Each view therefore keeps
     the shape it already speaks in, drawn in the palette's own pair for a
     distinguished item — no forced-color-adjust needed, these ARE system
     colours. The badge stays a fill rather than becoming a third ring, so a
     focused today cell shows focus AND today instead of one ring standing for
     both. */
  .is-stretched .nx-calendar-cell.is-today .nx-calendar-daynum {
    background: highlight;
    color: highlighttext;
  }

  .nx-calendar-cell.is-today {
    outline: calc(var(--nxt-hairline) * 3) solid highlight;
    outline-offset: calc(var(--nxt-hairline) * -3);
  }
}
</style>
